/**
 * Publication Item Extraction Renderer
 *
 * Displays item extraction batch process details:
 * operation description, target warehouse name, and settings grid.
 *
 * Data shape (from PublicationItemExtractionStartedEventSchema):
 *   publicationsIds: string[]
 *   warehouseId?: string
 *   fixedStock?: number
 *   ignoreStockForExistingItems?: boolean
 */

import { FC } from 'react';
import { Group, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconBuildingWarehouse } from '@tabler/icons-react';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, resolved, t }) => {
  const data = ( process.data || {}) as ProcessData;
  const warehouseId = data.warehouseId;
  const resolvedWarehouse = warehouseId ? resolved.resolvedWarehouses[warehouseId] : undefined;
  const publicationsCount = data.publicationsIds?.length ?? process.totalItems ?? 0;

  const pricedCount = process.pricedCount;
  const skippedForCurrencyCount = process.skippedForCurrencyCount;
  const hasPricingBreakdown =
    pricedCount != null || skippedForCurrencyCount != null;

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( 'batchProcesses_operations_PublicationItemExtraction_description' )}
      </Text>

      {warehouseId && (
        <Group gap="xs">
          <Text fz="sm" fw={500}>
            {t( 'batchProcesses_renderer_targetWarehouse' )}:
          </Text>
          <ThemeIcon size="sm" variant="light" color="orange">
            <IconBuildingWarehouse size={14} />
          </ThemeIcon>
          <Text fz="sm">
            {resolvedWarehouse?.name ?? warehouseId}
          </Text>
        </Group>
      )}

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_publicationCount' )}:
        </Text>
        <Text fz="sm">
          {publicationsCount}
        </Text>
      </Group>

      {/*
        ONE column below Mantine's `sm` (768px), two at and above it — the same
        threshold `useIsMobile()` uses, so this block collapses exactly where the
        rest of the surface switches. `SimpleGrid` defaults to `type="media"`, so
        this is a MEDIA query, not a container query: it introduces no
        `container-type` (and therefore no containing block for the detail page's
        docked action bar — the ticket-41 hazard). Desktop keeps two columns.

        At 320px the detail card has ~250px of content width and this row's two
        cells are "Ignorar stock existente: No" and "Stock fijo: 12" — two 125px
        columns clip both labels; stacked they read.
      */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
        <Group gap="xs">
          <Text fz="xs" fw={500}>
            {t( 'batchProcesses_renderer_ignoreStockExisting' )}:
          </Text>
          <Text fz="xs" c="dimmed">
            {data.ignoreStockForExistingItems ? t( 'batchProcesses_renderer_yes' ) : t( 'batchProcesses_renderer_no' )}
          </Text>
        </Group>

        {data.fixedStock != null && (
          <Group gap="xs">
            <Text fz="xs" fw={500}>
              {t( 'batchProcesses_renderer_fixedStock' )}:
            </Text>
            <Text fz="xs">
              {data.fixedStock}
            </Text>
          </Group>
        )}
      </SimpleGrid>

      {hasPricingBreakdown && (
        <Stack gap="xs">
          <Text fz="sm" fw={500}>
            {t( 'batchProcesses_renderer_pricingBreakdown' )}
          </Text>

          {/* Same responsive collapse as the settings grid above. */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {pricedCount != null && (
              <Group gap="xs">
                <Text fz="xs" fw={500}>
                  {t( 'batchProcesses_renderer_pricedCount' )}:
                </Text>
                <Text fz="xs">
                  {pricedCount}
                </Text>
              </Group>
            )}

            {skippedForCurrencyCount != null && (
              <Group gap="xs">
                <Text fz="xs" fw={500}>
                  {t( 'batchProcesses_renderer_skippedForCurrencyCount' )}:
                </Text>
                <Text fz="xs" c="dimmed">
                  {skippedForCurrencyCount}
                </Text>
              </Group>
            )}
          </SimpleGrid>
        </Stack>
      )}
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t, _process, _resolvedChannels, resolvedWarehouses ) => {
    const warehouseId = data?.warehouseId;
    if ( warehouseId ) {
      const resolved = resolvedWarehouses?.[warehouseId];
      const warehouseName = resolved?.name ?? warehouseId;
      return t( 'batchProcesses_summary_toWarehouse', { warehouse: warehouseName });
    }
    return t( 'batchProcesses_summary_unknownOperation' );
  },
  DetailComponent,
  getRequiredReferenceIds: ( process ) => {
    const data = ( process?.data || {}) as ProcessData;
    return {
      publicationIds: data.publicationsIds || [],
      warehouseIds: data.warehouseId ? [data.warehouseId] : []
    };
  }
};

export default DetailComponent;
